/**
 * The line under every finished chat answer, and the full text behind its link.
 *
 * **Very short on the line, whole behind the link** (owner, 2026-10-19). The line
 * repeats under every answer, so it is a reminder, not the terms; the terms are
 * the brand's own first-sign-in disclaimer (`lib/disclaimer.ts`), shown here
 * read-only — the same words the reader accepted, never a second copy that can
 * drift from them. A brand with no wording of its own gets QuFolio's under its
 * name, as its sign-in card does.
 */

import { useEffect, useState } from "react";

import { BRAND } from "../../lib/brand-current";
import { disclaimerFor } from "../../lib/disclaimer";

export const CHAT_DISCLAIMER = "QueVee (AI) can be wrong · Education only, not advice";

export function ChatDisclaimerLine({ brandKey = BRAND.key }: { brandKey?: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={LINE}>
      {CHAT_DISCLAIMER} ·{" "}
      <button type="button" style={LINK} onClick={() => setOpen(true)}>
        Disclaimer
      </button>
      {open && <ChatDisclaimerText brandKey={brandKey} onClose={() => setOpen(false)} />}
    </div>
  );
}

function ChatDisclaimerText({ brandKey, onClose }: { brandKey: string; onClose: () => void }) {
  const text = disclaimerFor(brandKey);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <>
      <div style={SCRIM} onPointerDown={onClose} />
      <div role="dialog" aria-modal="true" aria-label={text.title} style={BOX}>
        <div style={HEAD}>
          <b>{text.title}</b>
          <button type="button" style={LINK} aria-label="Close disclaimer" onClick={onClose}>
            ✕
          </button>
        </div>
        {text.heading ? <p style={{ fontWeight: 600 }}>{text.heading}</p> : null}
        <p>{text.lead}</p>
        <ul style={{ paddingLeft: 18, margin: "6px 0" }}>
          {text.bullets.map((b) => (
            <li key={b}>{b}</li>
          ))}
        </ul>
        {text.short.map((s) => (
          <p key={s}>{s}</p>
        ))}
        {text.full.length > 0 && <p style={{ fontWeight: 600, marginTop: 10 }}>Full disclaimer</p>}
        {text.full.map((f) => (
          <p key={f}>{f}</p>
        ))}
        <p>
          <b>{text.use.heading}</b> {text.use.body}
        </p>
      </div>
    </>
  );
}

const LINE: React.CSSProperties = { fontSize: 10.5, color: "var(--qf-text-muted)", marginTop: 2 };
const LINK: React.CSSProperties = {
  border: 0,
  background: "transparent",
  color: "var(--qf-accent)",
  cursor: "pointer",
  padding: 0,
  font: "inherit",
};
const SCRIM: React.CSSProperties = { position: "fixed", inset: 0, zIndex: 2999, background: "rgba(0,0,0,0.35)" };
const BOX: React.CSSProperties = {
  position: "fixed",
  zIndex: 3000,
  left: "50%",
  top: "50%",
  transform: "translate(-50%, -50%)",
  width: "min(560px, calc(100vw - 32px))",
  maxHeight: "80vh",
  overflowY: "auto",
  padding: "12px 16px",
  background: "var(--qf-surface)",
  color: "var(--qf-text-primary)",
  border: "1px solid var(--qf-border)",
  borderRadius: 8,
  boxShadow: "0 10px 30px rgba(0,0,0,0.35)",
  fontSize: 13,
  lineHeight: 1.45,
};
const HEAD: React.CSSProperties = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 };
